<template>
    <AppHead title="Neue Veranstaltung eingeben" />
    <div class="lg:px-64">
        <PageTitle><span v-if="date">Veranstaltung bearbeiten</span><span v-else>Neue Veranstaltung eingeben</span> - Schritt 2 von 3</PageTitle>
        <form @submit.prevent="submit">
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="category" value="Rubrik" />
                    <FormInput type="text" :value="category" class="mt-1 block w-full text-gray-500" readonly />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Der Name der Rubrik dient zur Orientierung und kann hier nicht geändert werden.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="location" value="Ort" />
                    <FormInput type="text" :value="location" class="mt-1 block w-full text-gray-500" readonly />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Der Name des Ortes dient zur Orientierung und kann hier nicht geändert werden.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="name" value="Name" />
                    <FormInput type="text" :value="name" class="mt-1 block w-full text-gray-500" readonly />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Der Name der Veranstaltung dient zur Orientierung und kann hier nicht geändert werden.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <div>
                        <FormLabel for="image.file" value="Bild-Datei" />
                        <FormInput id="image.file" type="file" class="mt-1 block w-full" @input="form.image.file = $event.target.files[0]" />
                        <FormError v-if="form.hasErrors && form.errors['image.file']" :message="form.errors['image.file']" />
                    </div>
                    <ul v-if="media.length" class="mt-2 list-disc">
                        <li class="text-red-600 font-bold list-none">Sie haben folgende Bilder für diese Veranstaltung bereits zugeordnet:</li>
                        <li v-for="name in media" class="ml-4">{{ name }}</li>
                    </ul>
                    <div class="mt-4">
                        <FormLabel for="image.name" value="Bild-Name" />
                        <FormInput id="image.name" type="text" class="mt-1 block w-full" v-model="form.image.name" :required="form.image.file ? true : false" />
                        <FormError v-if="form.hasErrors && form.errors['image.name']" :message="form.errors['image.name']" />
                    </div>
                    <div class="mt-4">
                        <FormLabel for="image.copyright" value="Bild-Copyright" />
                        <FormInput id="image.copyright" type="text" class="mt-1 block w-full" v-model="form.image.copyright" :required="form.image.file ? true : false" />
                        <FormError v-if="form.hasErrors && form.errors['image.copyright']" :message="form.errors['image.copyright']" />
                    </div>
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    <p class="mb-4">Wenn Sie kein Bild hochladen, wird ein Platzhalterbild vom Kulturkalender verwendet.</p>
                    <p>Suchen Sie ein Veranstaltungsbild auf Ihrem Computer und fügen es hier mit folgenden Eigenschaften ein:</p>
                    <ul class="list-disc list-inside">
                        <li>Format: Querformat</li>
                        <li>Dateiformat: jpg</li>
                        <li>Auflösung: max. 96 dpi</li>
                        <li>Bildgröße in Pixel (px): mind. 800 px breit</li>
                        <li>Bestes Ergebnis: 1.200 px mal 675 px (B x H)</li>
                    </ul>
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="video" value="Video" />
                    <FormInputUrl id="video" class="mt-1 block w-full" v-model="form.video" placeholder="https://" />
                    <FormError v-if="form.hasErrors && form.errors.video" :message="form.errors.video" />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Wenn Sie ein Video hochladen möchten, kopieren Sie den vollständigen Link aus YouTube oder Vimeo in das Feld. Das Video bleibt in jedem Fall auf der
                    gewählten Plattform. Das Video wird von dort gestreamt. (Framing)
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2 flex items-center justify-end space-x-4 mt-4">
                    <Link as="button" :href="route('manager.event.edit', [event, date])" class="btn-primary">
                        Zurück zu Schritt 1
                    </Link>
                    <FormButton :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        Weiter zu Schritt 3
                    </FormButton>
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-4">
                    Wenn Sie ein Bild löschen möchten, senden Sie uns bitte eine Mail an:<br>
                    <a href="mailto:kuka-manager@kulturkalender-dresden.de">kuka-manager@kulturkalender-dresden.de</a>
                </div>
            </div>
            <div class="w-full py-2">
                <progress v-if="form.progress" :value="form.progress.percentage" max="100" class="w-full">
                    {{ form.progress.percentage }}%
                </progress>
            </div>
        </form>
    </div>
</template>

<script setup>
import { Link, useForm } from "@inertiajs/vue3";
import AppHead from '@/Shared/Head';
import PageTitle from '@/Shared/Page/Title';
import FormError from '@/Shared/Form/Error';
import FormLabel from '@/Shared/Form/Label';
import FormInput from '@/Shared/Form/Input';
import FormButton from '@/Shared/Form/Button';
import FormInputUrl from '@/Shared/Form/InputUrl';

const props = defineProps({
    event: String,
    category: String,
    location: String,
    name: String,
    date: Number,
    media: Array,
})
const form = useForm({
    image: {
        file: '',
        name: '',
        copyright: '',
    },
    video: '',
});
const submit = () => {
    form.post(route('manager.media.store', [props.event, props.date]), {
        preserveScroll: true,
        onError: () => {
            document.getElementById('image.file').value = form.image.file = null;
        }
    });
};
</script>
